"use client"

import { useState, type ReactNode } from "react"
import Image from "next/image"
import { cn } from "@/shared/lib/utils"
import { captureClientException } from "@/shared/lib/monitoring/capture-client"

/**
 * Branded placeholder shown when an image is missing or fails to load. It's a
 * same-origin static asset, so it always resolves — a failed avatar or gallery
 * image degrades to brand art instead of rendering as a black box.
 */
const BRANDED_FALLBACK_SRC = "/images/paywall/generate-default.png"

type ImageWithFallbackProps = {
  src: string
  alt: string
  className?: string
  /** Classes for the loading shimmer placeholder. */
  fallbackClassName?: string
  /**
   * Suppress the pulsing loading shimmer. Use where the surface behind the image
   * already reads as a deliberate loading state (e.g. a dark hero canvas), so a
   * gray pulse would be a visual regression rather than an improvement.
   */
  disableShimmer?: boolean
  /** DOM fallback used after the image fails twice. */
  fallback?: ReactNode
  sizes?: string
  priority?: boolean
  /** Bypass Next.js image optimization (matches `next/image`'s `unoptimized`). */
  unoptimized?: boolean
  onLoad?: () => void
  errorScope?: string
  errorExtra?: Record<string, unknown>
} & (
  | { fill: true; width?: never; height?: never }
  | { fill?: false; width: number; height: number }
)

export function ImageWithFallback({
  src,
  alt,
  className,
  fallbackClassName,
  disableShimmer,
  fallback,
  sizes,
  priority,
  unoptimized,
  onLoad,
  errorScope,
  errorExtra,
  ...dims
}: ImageWithFallbackProps) {
  const [erroredSrc, setErroredSrc] = useState<string | null>(null)
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const [retriedSrc, setRetriedSrc] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  // Reset per-image state when `src` genuinely changes, synchronously during
  // render (not in a post-commit effect). A passive effect would also run on
  // mount and could clobber a `loadedKey` that `onLoad` already set for a
  // browser-cache hit, stranding the image at opacity-0. Adjusting state during
  // render only fires on a real `src` change, so cached images stay visible.
  const [trackedSrc, setTrackedSrc] = useState(src)
  if (src !== trackedSrc) {
    setTrackedSrc(src)
    setErroredSrc(null)
    setLoadedKey(null)
    setRetriedSrc(null)
    setReloadKey(0)
  }

  // On failure (or empty src) swap to the branded placeholder. The guard stops
  // an infinite error loop if the placeholder itself ever failed to load.
  const shouldUseFallback = !src || erroredSrc === src
  const customFallback = shouldUseFallback && fallback != null ? fallback : null
  const resolvedSrc = shouldUseFallback ? BRANDED_FALLBACK_SRC : src
  const imageKey = `${resolvedSrc}:${reloadKey}`
  const isLoading = !customFallback && loadedKey !== imageKey

  if (customFallback) {
    if (dims.fill) return <>{customFallback}</>

    return (
      <div className="relative" style={{ width: dims.width, height: dims.height }}>
        {customFallback}
      </div>
    )
  }

  const img = (
    <Image
      key={imageKey}
      src={resolvedSrc}
      alt={alt}
      {...(dims.fill
        ? { fill: true as const }
        : { width: dims.width, height: dims.height })}
      sizes={sizes}
      priority={priority}
      unoptimized={unoptimized}
      className={cn(
        "transition-opacity duration-300",
        isLoading ? "opacity-0" : "opacity-100",
        className
      )}
      onError={() => {
        if (resolvedSrc === BRANDED_FALLBACK_SRC) {
          setLoadedKey(imageKey)
          return
        }

        if (retriedSrc !== resolvedSrc) {
          setRetriedSrc(resolvedSrc)
          setLoadedKey(null)
          setReloadKey((key) => key + 1)
          return
        }

        if (errorScope) {
          captureClientException(
            new Error(`image failed to load: ${errorScope}`),
            {
              scope: errorScope,
              extra: {
                ...(errorExtra ?? {}),
                image_status: "error",
              },
            }
          )
        }

        setErroredSrc(resolvedSrc)
      }}
      onLoad={() => {
        setLoadedKey(imageKey)
        onLoad?.()
      }}
    />
  )

  const shimmer = isLoading && !disableShimmer && (
    <div
      className={cn(
        "absolute inset-0 animate-pulse bg-muted/60",
        fallbackClassName
      )}
    />
  )

  // Fill mode: the parent owns positioning/size, so render into it directly.
  if (dims.fill) {
    return (
      <>
        {shimmer}
        {img}
      </>
    )
  }

  return (
    <div className="relative" style={{ width: dims.width, height: dims.height }}>
      {shimmer}
      {img}
    </div>
  )
}
